import { Component, input, output, signal, computed, ChangeDetectionStrategy, effect } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CardViewConfig, CardFieldConfig } from '../types';
import { ZagCardComponent, ZagCardTitleComponent, ZagCardBodyComponent, ZagCardFooterComponent } from '../zag-card';
import { Avatar } from '../../avatar/avatar';
import { CardSearchComponent } from '../card-search/card-search.component';
import { ZagButton } from '../../button/button';
import { ZagIcon } from '../../icon/icon';

/** Convert an unknown value to a display string with safe type guards */
function valueToString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

@Component({
  selector: 'zag-generic-card-view',
  imports: [
    TranslateModule,
    ZagCardComponent,
    ZagCardTitleComponent,
    ZagCardBodyComponent,
    ZagCardFooterComponent,
    Avatar,
    CardSearchComponent,
    ZagButton,
    ZagIcon,
  ],
  templateUrl: './generic-card-view.component.html',
  styleUrls: ['./generic-card-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericCardViewComponent<T = unknown> {
  /**
   * Array of data items to display
   */
  data = input.required<T[]>();

  /**
   * Card view configuration
   */
  config = input.required<CardViewConfig<T>>();

  /**
   * Number of items per page (0 = show all)
   */
  pageSize = input<number>(0);

  /**
   * Loading state
   */
  loading = input<boolean>(false);

  /**
   * Emitted when a card is clicked
   */
  cardClick = output<T>();

  /**
   * Internal search term
   */
  searchTerm = signal<string>('');

  /**
   * Current page index
   */
  currentPage = signal<number>(0);

  /**
   * Focused card index for keyboard navigation
   */
  focusedCardIndex = signal<number>(-1);

  /**
   * Filtered data based on search
   */
  filteredData = computed(() => {
    const data = this.data();
    const config = this.config();
    const search = this.searchTerm().toLowerCase().trim();

    if (!search || !config.searchConfig?.enabled) {
      return data;
    }

    const searchableFields = config.fields.filter(f => f.searchable);
    if (searchableFields.length === 0) {
      return data;
    }

    return data.filter(item => {
      const searchMode = config.searchConfig?.mode || 'OR';
      const fuzzy = config.searchConfig?.fuzzySearch !== false;
      const searchTerms = search.split(' ').filter(t => t.length > 0);

      const matches = searchableFields.map(field => {
        const value = this.getFieldValue(item, field.key);
        const rawValue = valueToString(value);
        const formattedValue = field.formatter ? field.formatter(value, item) : rawValue.toLowerCase();

        if (fuzzy) {
          return searchTerms.some(term => formattedValue.includes(term));
        } else {
          return searchTerms.includes(formattedValue);
        }
      });

      if (searchMode === 'AND') {
        return matches.every(Boolean);
      } else {
        return matches.some(Boolean);
      }
    });
  });

  /**
   * Paginated data
   */
  paginatedData = computed(() => {
    const filtered = this.filteredData();
    const size = this.pageSize();
    const page = this.currentPage();

    if (size === 0) {
      return filtered;
    }

    const start = page * size;
    const end = start + size;
    return filtered.slice(start, end);
  });

  /**
   * Total number of pages
   */
  totalPages = computed(() => {
    const size = this.pageSize();
    if (size === 0) return 1;
    return Math.ceil(this.filteredData().length / size);
  });

  /**
   * Has previous page
   */
  hasPreviousPage = computed(() => this.currentPage() > 0);

  /**
   * Has next page
   */
  hasNextPage = computed(() => this.currentPage() < this.totalPages() - 1);

  constructor() {
    // Reset to first page when data or search changes
    effect(() => {
      this.filteredData();
      this.currentPage.set(0);
      this.focusedCardIndex.set(-1);
    });
  }

  /**
   * Navigate to previous page
   */
  previousPage(): void {
    if (this.hasPreviousPage()) {
      this.currentPage.update(p => p - 1);
    }
  }

  /**
   * Navigate to next page
   */
  nextPage(): void {
    if (this.hasNextPage()) {
      this.currentPage.update(p => p + 1);
    }
  }

  /**
   * Handle card click
   */
  onCardClick(item: T): void {
    this.cardClick.emit(item);
  }

  /**
   * Get fields for a specific position
   */
  getFieldsForPosition(position: 'header' | 'body' | 'footer'): CardFieldConfig<T>[] {
    return this.config().fields.filter(f => f.position === position);
  }

  /**
   * Get value from item using field key
   */
  getFieldValue(item: T, key: keyof T | string): unknown {
    return (item as Record<string, unknown>)[String(key)];
  }

  /**
   * Format field value
   */
  formatFieldValue(item: T, field: CardFieldConfig<T>): string {
    const value = this.getFieldValue(item, field.key);
    if (field.formatter) {
      try {
        return field.formatter(value, item);
      } catch (error) {
        console.error(`Error in field formatter for key "${String(field.key)}":`, error);
        return valueToString(value);
      }
    }
    return valueToString(value);
  }

  /**
   * Check if field is visible
   */
  isFieldVisible(item: T, field: CardFieldConfig<T>): boolean {
    if (!field.visible) {
      return true;
    }
    return field.visible(item);
  }

  /**
   * Get CSS class for field
   */
  getFieldCssClass(item: T, field: CardFieldConfig<T>): string {
    if (!field.cssClass) {
      return '';
    }
    if (typeof field.cssClass === 'function') {
      return field.cssClass(item);
    }
    return field.cssClass;
  }

  /**
   * Handle button field click
   */
  onButtonClick(item: T, field: CardFieldConfig<T>, event: Event): void {
    event.stopPropagation();
    if (field.onClick) {
      field.onClick(item, event);
    }
  }

  /**
   * Get primary key value for tracking
   */
  trackByFn = (index: number, item: T): unknown => {
    const key = this.config().primaryKey;
    return (item as Record<string, unknown>)[key as string] ?? index;
  };

  /**
   * Handle keyboard navigation
   */
  onKeyDown(event: KeyboardEvent): void {
    const cards = this.paginatedData();
    if (cards.length === 0) return;

    const current = this.focusedCardIndex();
    let newIndex = current;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        newIndex = current < cards.length - 1 ? current + 1 : current;
        event.preventDefault();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        newIndex = current > 0 ? current - 1 : 0;
        event.preventDefault();
        break;
      case 'Home':
        newIndex = 0;
        event.preventDefault();
        break;
      case 'End':
        newIndex = cards.length - 1;
        event.preventDefault();
        break;
      case 'Enter':
      case ' ':
        if (current >= 0 && this.config().clickable) {
          this.onCardClick(cards[current]);
          event.preventDefault();
        }
        break;
    }

    if (newIndex !== current) {
      this.focusedCardIndex.set(newIndex);
    }
  }
}
